import { isFuture, parseISO, safeParseISO } from 'shared/util/datetime-fns';
import type { Money } from 'shared/util/money';
import { RentalStatusType } from './rentalStatus';

export interface BookingArchive {
    bookingReference: string;
    endDate: Date;
    itemId: number;
    itemImage?: string;
    itemTitle?: string;
    deadlineTimestamp?: Date;
    startDate: Date;
    status: RentalStatusType;
    totalPrice: Money;
    firstName: string;
    lastName: string;
}

export const createArchiveData = (data: Record<string, unknown>[]): BookingArchive[] =>
    data.map(
        (booking): BookingArchive => ({
            bookingReference: String(booking.bookingReference),
            endDate: parseISO(booking.endDate as string),
            itemId: Number(booking.itemId),
            itemImage: booking.itemImage ? String(booking.itemImage) : undefined,
            itemTitle: booking.itemTitle ? String(booking.itemTitle) : undefined,
            deadlineTimestamp: safeParseISO(booking.deadlineTimestamp),
            startDate: parseISO(booking.startDate as string),
            status: booking.status as RentalStatusType,
            totalPrice: booking.totalPrice as Money,
            firstName: booking.firstName ? String(booking.firstName) : '',
            lastName: booking.lastName ? String(booking.lastName) : '',
        }),
    );

const pendingConfirmationBookingsStatus = [RentalStatusType.CREATED];

const pendingPaymentBookingsStatus = [RentalStatusType.CONFIRMED, RentalStatusType.CONFIRMED_WITH_AMENDMENT];

const inProgressBookingsStatus = [
    RentalStatusType.UPFRONT_PAYMENT_PAID,
    RentalStatusType.REMAINING_PAYMENT_READY,
    RentalStatusType.REMAINING_PAYMENT_PAID,
    RentalStatusType.FULL_PAYMENT_PAID,
    RentalStatusType.STAY_IN_PROGRESS,
];

const archivedBookingsStatus = [
    RentalStatusType.STAY_COMPLETED,
    RentalStatusType.CONFIRMATION_EXPIRED,
    RentalStatusType.UPFRONT_PAYMENT_EXPIRED,
    RentalStatusType.REMAINING_PAYMENT_EXPIRED,
    RentalStatusType.FULL_PAYMENT_EXPIRED,
    RentalStatusType.REFUNDED,
    RentalStatusType.REJECTED_BY_HOST,
    RentalStatusType.WITHDRAWN_BY_HOST,
    RentalStatusType.WITHDRAWN_BY_TENANT,
    RentalStatusType.CANCELLED_BY_HOST,
    RentalStatusType.CANCELLED_BY_TENANT,
];

const eitherArchivedOrInProgressStatus = [
    RentalStatusType.PAYOUT_INITIATED,
    RentalStatusType.PAYOUT_COMPLETED,
    RentalStatusType.SETTLED_WITH_REFUND,
];

export const isPendingConfirmation = (booking: BookingArchive): boolean => pendingConfirmationBookingsStatus.includes(booking.status);

export const isPendingPayment = (booking: BookingArchive): boolean => pendingPaymentBookingsStatus.includes(booking.status);

export const isInProgress = (booking: BookingArchive): boolean =>
    inProgressBookingsStatus.includes(booking.status) ||
    (eitherArchivedOrInProgressStatus.includes(booking.status) && isFuture(booking.endDate));

export const isArchived = (booking: BookingArchive): boolean =>
    archivedBookingsStatus.includes(booking.status) ||
    (eitherArchivedOrInProgressStatus.includes(booking.status) && !isFuture(booking.endDate));

export const isCancelled = (booking: BookingArchive): boolean => {
    const cancelledStatuses: RentalStatusType[] = archivedBookingsStatus.filter((status) => status !== RentalStatusType.STAY_COMPLETED);
    return cancelledStatuses.includes(booking.status);
};
